import { first, last } from 'lodash';

import {
  TrackedShipmentEvent,
  TrackedShipmentRouteSegment,
  TrackedShipmentState,
  TrackedShipmentStop,
} from '@project44-models/shipment-entity';

import { Result, Segment, Stop, Updates } from '../types';

const filterEvents = (events: TrackedShipmentEvent[]) => {
  return events.filter((i) => {
    return !(i.type === 'INFO' && i.description === 'Seen at a new location.');
  });
};

export const getCurrentRouteSegmentId = (
  results: Result[],
  events: TrackedShipmentEvent[],
  states: TrackedShipmentState[],
) => {
  const filteredEvents = filterEvents(events);
  const withCurrentEvents = results.filter((result) => {
    const stopEvents = filteredEvents.filter(
      (e) =>
        e[result.type === 'stop' ? 'stopId' : 'routeSegmentId'] === result.id,
    );
    return stopEvents.some((e) => !!e.dateTime);
  });

  const currentSegmentByEvents = last(withCurrentEvents)
    ? last(withCurrentEvents)?.id
    : null;

  return currentSegmentByEvents || last(states)?.stopId || null;
};

export const deduplicateEvents = (data: Result[]): Result[] => {
  const usedEvents: TrackedShipmentEvent[] = [];

  return data.reduce((result: Result[], stop: Result, idx: number) => {
    const hasDuplicateStops = data.filter((i) => i.id === stop.id).length > 1;
    if (!hasDuplicateStops) return [...result, stop];

    const nextStops = data.slice(idx + 1).filter((s) => s.id !== stop.id);
    const nextFirstActualEvent = first(
      nextStops
        .flatMap((s) => s.events)
        .filter((e) => !!e.dateTime)
        .map((e) => e.dateTime),
    );

    stop.events = (stop.events || [])
      .filter((e) => !usedEvents.includes(e))
      .filter(
        (e) =>
          !e.dateTime ||
          new Date(e.dateTime) < new Date(nextFirstActualEvent || new Date()),
      );

    usedEvents.push(...stop.events);
    if (stop.events.length) result.push(stop);

    return result;
  }, []);
};

export const getShipmentRoute = (
  stops: TrackedShipmentStop[],
  segments: TrackedShipmentRouteSegment[],
  events: TrackedShipmentEvent[] = [],
): Result[] => {
  const filteredEvents = filterEvents(events);
  const typedStops = stops.map((stop) => ({
    ...stop,
    originalType: stop.type,
    events: filteredEvents.filter((event) => event.stopId === stop.id),
    type: 'stop',
  }));

  const typedSegments = segments.map((segment) => ({
    ...segment,
    type: 'routeSegment',
    events: filteredEvents.filter((event) => event.stopId === segment.id),
  }));

  const data = typedSegments
    .reduce<unknown[]>((acc, segment) => {
      const fromStop = typedStops.find((s) => segment.fromStopId === s.id);
      const toStop = typedStops.find((s) => segment.toStopId === s.id);

      const isFromStopPresent = !!acc.find(
        (a) => segment.fromStopId === (a as TrackedShipmentStop).id,
      );

      if (isFromStopPresent) return [...acc, segment, { ...toStop }];

      return [...acc, { ...fromStop }, segment, { ...toStop }];
    }, [])
    .filter((item) => item) as Result[];

  return deduplicateEvents(data);
};

const getDate = (e: TrackedShipmentEvent) => {
  if (!e.dateTime) return new Date();
  return new Date(String(e.dateTime));
};

const getRouteResultTimebox = (result: Result) => {
  const dates = result.events.filter((i) => !!i.dateTime).map(getDate);
  if (!dates.length) return [null, null];

  return [
    new Date(Math.min(...dates.map(Number))),
    new Date(Math.max(...dates.map(Number))),
  ];
};

const hasSegmentsWithActualDates = (route: Result[]) => {
  return route.some((res) => getRouteResultTimebox(res)[0]);
};

const getNonStopIdEvents = (events: TrackedShipmentEvent[]) => {
  return events.filter((e) => !e.stopId).filter((i) => i.type !== 'UNKNOWN');
};

const eventUpdates = (events: TrackedShipmentEvent[]): Updates => {
  return { type: 'updates', events };
};

export const getRouteWithEvents = (
  route: Result[],
  events: TrackedShipmentEvent[],
): Result[] => {
  const nsEvents = getNonStopIdEvents(events);

  const data = route.reduce((acc, result, idx) => {
    const atStopEvents = [];
    const prevEvents = [];

    if (result.type === 'stop') {
      const [min, max] = getRouteResultTimebox(result);

      if (min && max) {
        while (nsEvents.length && getDate(nsEvents[0]) < min) {
          prevEvents.push(nsEvents.shift() as TrackedShipmentEvent);
        }
        while (nsEvents.length && getDate(nsEvents[0]) < max) {
          atStopEvents.push(nsEvents.shift() as TrackedShipmentEvent);
        }
      } else if (!hasSegmentsWithActualDates(route.slice(idx))) {
        while (nsEvents.length) {
          prevEvents.push(nsEvents.shift() as TrackedShipmentEvent);
        }
      }
    }

    prevEvents.length && acc.push(eventUpdates(prevEvents));
    acc.push(result);
    atStopEvents.length && acc.push(eventUpdates(atStopEvents));

    return acc;
  }, [] as Result[]);

  if (nsEvents.length) {
    data.push(eventUpdates(nsEvents));
  }

  return data;
};

export const getHasPastEvents = (
  events: TrackedShipmentEvent[],
  result: Result,
) => {
  const stopId =
    result.type === 'stop'
      ? (result as unknown as Stop).id
      : (result as unknown as Segment).toStopId;

  return events
    .filter((event) => event.stopId === stopId)
    .some((event) => !!event.dateTime);
};
